import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Copy, MessageSquare, Check, Clock } from "lucide-react";
import { AppHeader, SourceNote } from "@/components/Layout";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const Stat = ({ label, value, tone = "text-slate-900" }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5">
    <p className="label-caps">{label}</p>
    <p className={`mt-2 font-display text-3xl font-extrabold tracking-tighter ${tone}`}>{value}</p>
  </div>
);

export default function Referrals() {
  useSeo({ title: "Refer a friend", description: "Share Food Recall Alerts and earn a free month.", path: "/referrals", noindex: true });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/referrals/me").then(setData).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.link);
      toast.success("Link copied");
    } catch { toast.error("Could not copy - select the link and copy it manually"); }
  };

  const useCredit = async () => {
    setBusy(true);
    try {
      await api.post("/referrals/apply", {});
      toast.success("Free month applied - your next payment will be skipped");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!data) return (<div className="min-h-screen bg-white"><AppHeader /><p className="p-10 text-slate-500">Loading...</p></div>);

  return (
    <div className="min-h-screen bg-white">
      <AppHeader />
      <div className="mx-auto max-w-3xl px-5 py-10 sm:py-14" data-testid="referrals-page">
        <p className="label-caps">Refer a friend</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tighter text-slate-900 sm:text-5xl">
          Give a free month, get a free month.
        </h1>
        <p className="mt-4 text-slate-600">
          Send your link to someone who should know about food recalls. When they finish signing up,
          you get one billing cycle free.
        </p>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5 sm:p-6">
          <p className="label-caps">Your link</p>
          <p className="mt-2 break-all font-mono text-xs text-slate-900 sm:text-sm" data-testid="referral-link">{data.link}</p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <button data-testid="copy-referral-link" onClick={copy}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800 sm:py-2.5">
              <Copy className="h-4 w-4" /> Copy link
            </button>
            <a data-testid="sms-referral-link" href={`sms:?&body=${encodeURIComponent(data.share_text)}`}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-800 transition-colors hover:bg-white sm:py-2.5">
              <MessageSquare className="h-4 w-4" /> Share by text
            </a>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <Stat label="Friends signed up" value={data.signups.length} />
          <Stat label="Free months pending" value={data.pending_months} tone="text-red-700" />
          <Stat label="Free months used" value={data.used_months} tone="text-emerald-600" />
        </div>

        {data.pending_months > 0 && (
          <div className="mt-6 flex flex-wrap items-center gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-6" data-testid="pending-credit-box">
            <p className="text-sm text-amber-900">
              You have <span className="font-semibold">{data.pending_months}</span> free{" "}
              {data.pending_months === 1 ? "month" : "months"} waiting. One is applied at a time and
              skips your next payment.
            </p>
            <button data-testid="use-credit-btn" disabled={busy} onClick={useCredit}
              className="w-full rounded-full bg-amber-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-800 disabled:opacity-50 sm:ml-auto sm:w-auto">
              Apply a free month
            </button>
          </div>
        )}

        <h2 className="mt-12 font-display text-lg font-semibold tracking-tight text-slate-900">
          Who has joined
        </h2>
        {data.signups.length === 0 ? (
          <p className="mt-3 rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600" data-testid="referrals-empty">
            Nobody yet. Share your link above and your first free month lands here.
          </p>
        ) : (
          <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200" data-testid="referral-list">
            {data.signups.map((s) => (
              <div key={s.mobile + s.joined_at}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-100 px-4 py-4 last:border-b-0 sm:px-5">
                <Gift className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="font-mono text-sm font-semibold text-slate-900">{s.mobile}</span>
                <span className="text-xs text-slate-500">joined {fmtDate(s.joined_at)}</span>
                <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold sm:ml-auto ${
                  s.status === "applied" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                  {s.status === "applied"
                    ? <><Check className="h-3 w-3" /> Free month used</>
                    : <><Clock className="h-3 w-3" /> Free month pending</>}
                </span>
              </div>
            ))}
          </div>
        )}

        <p className="mt-6 text-xs text-slate-500">
          A free month is applied to your subscription as a one-off 100% discount, so that billing
          cycle is skipped. You need an active subscription for a credit to be applied - credits keep
          waiting until you do.
        </p>
        <SourceNote className="mt-10" />
      </div>
    </div>
  );
}
